<template>
  <div class="count-input__wrp">
    <slot name="label">
      <ui-label
        v-if="label"
        :name="name"
        :plan-limits="planLimits"
        :text="label"
        :help-tooltip="helpTooltip"
      />
    </slot>
    <div class="count-input">
      <v-input
        :disabled="disabled"
        center-affix
        hide-details
        hide-spin-buttons
        density="compact"
        :error-messages="errorMessages"
      >
        <template #prepend>
          <ui-icon
            class="count-input__icon"
            icon="fas fa-minus"
            size="12"
            :disabled="disabled"
            @click="clickMinus"
          />
        </template>
        <input
          v-model="inputValue"
          type="number"
          :readonly="!planLimitsEditable && showPlanLimitsIcon"
          :disabled="disabled"
          @click="inputClick"
          @blur="setInnerValueFromInput"
        >
        <template #append>
          <ui-icon
            class="count-input__icon"
            icon="fas fa-plus"
            size="12"
            :disabled="disabled"
            @click="clickPlus"
          />
        </template>
      </v-input>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { integer } from '@vuelidate/validators';
import { usePlanLimits, type PlanLimitsProp } from '@/composables';

const props = withDefaults(
  defineProps<{
    value?: number;
    modelValue?: number;
    min?: number | null;
    max?: number | null;
    label?: string;
    name?: string;
    disabled?: boolean;
    helpTooltip?: string;
    hasErrors?: boolean;
    planLimits?: PlanLimitsProp;
    planLimitsEditable?: boolean;
  }>(),
  {
    value: 0,
    min: null,
    max: null,
    disabled: false,
    helpTooltip: '',
    hasErrors: false,
    planLimitsEditable: false,
  },
);

const emit = defineEmits<{
  input: [value: number];
  'update:modelValue': [value: number];
}>();

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  () => props.planLimitsEditable,
);

const errorMessages = computed(() => (props.hasErrors ? 'error' : null));

const inputValue = ref('0');
const innerValue = ref(0);

const externalValue = computed(() =>
  props.modelValue !== undefined ? props.modelValue : props.value,
);

const clampValue = (value: number) => {
  if (props.min !== null && value < props.min) {
    return props.min;
  }
  if (props.max !== null && value > props.max) {
    return props.max;
  }
  return value;
};

const updateInputValue = () => {
  inputValue.value = String(innerValue.value);
};

const commitInnerValue = (value: number) => {
  innerValue.value = clampValue(value);
  updateInputValue();
  emit('input', innerValue.value);
  emit('update:modelValue', innerValue.value);
};

watch(
  externalValue,
  (value) => {
    innerValue.value = clampValue(value);
    updateInputValue();
  },
  { immediate: true },
);

const setInnerValueFromInput = () => {
  if (integer.$validator(inputValue.value)) {
    commitInnerValue(Number(inputValue.value));
  } else {
    updateInputValue();
  }
};

const clickPlus = () => {
  if (!props.planLimitsEditable && showPlanLimitsIcon.value) {
    return handleClickPlanLimits();
  }
  commitInnerValue(innerValue.value + 1);
};

const clickMinus = () => {
  if (!props.planLimitsEditable && showPlanLimitsIcon.value) {
    return handleClickPlanLimits();
  }
  commitInnerValue(innerValue.value - 1);
};

const inputClick = () => {
  if (!props.planLimitsEditable && showPlanLimitsIcon.value) {
    return handleClickPlanLimits();
  }
};
</script>

<style lang="scss" scoped>
.count-label {
  color: var(--color-graphite);
  font-weight: bold !important;
}

.plan-limits-icon {
  margin-left: 4px;
}
</style>

<style lang="scss">
.count-input {
  display: flex;
  max-width: max-content !important;
}

.count-input__wrp {
  max-width: max-content !important;

  &__label {
    font-size: 13px;
    font-weight: bold;
    color: var(--color-black);
  }

  .v-input__details,
  .v-messages {
    display: none;
  }

  .v-input__prepend,
  .v-input__append,
  .v-input__prepend-outer,
  .v-input__append-outer,
  .v-input__slot {
    margin: 0;
  }

  .v-input__prepend,
  .v-input__append,
  .v-input__prepend-outer,
  .v-input__append-outer {
    padding: 0;
    min-width: 24px;
    justify-content: center;
    cursor: pointer;
  }

  .count-input__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    min-width: 24px;
    cursor: pointer;
    color: var(--color-black);

    &.disabled {
      cursor: default;
    }
  }

  .v-input__slot {
    padding: 3px 0;
  }

  .v-input__control {
    border-left: 1px solid var(--color-gray10);
    border-right: 1px solid var(--color-gray10);
    padding: 0 8px;
    min-height: 24px;
    align-items: center;

    input {
      width: 100%;
      font-size: 13px;
      text-align: center;
      border: none !important;
      outline: none;
      background: transparent;
      padding: 0;
      min-height: 22px;
      -moz-appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  .v-input {
    --v-input-control-height: 24px;
    --v-input-padding-top: 0;
    border: 1px solid var(--color-gray10);
    background: #fff;
    width: 110px;
    border-radius: 6px;
    flex: none;
    font-size: 13px;

    &.v-input--horizontal {
      .v-input__prepend,
      .v-input__append {
        margin-inline: 0;
      }
    }

    .count-input__icon,
    .v-input__prepend > .v-icon,
    .v-input__append > .v-icon,
    .v-icon {
      font-size: 12px;
      height: 100%;
      width: 100%;
      opacity: 1;
      color: var(--color-black);
    }

    &.v-input--error,
    &.error--text {
      border-color: var(--color-red);

      .v-input__control {
        border-left-color: var(--color-red);
        border-right-color: var(--color-red);
      }

      input {
        border-style: none !important;
        caret-color: initial !important;
      }

      .count-input__icon,
      .v-input__prepend > .v-icon,
      .v-input__append > .v-icon,
      .v-icon {
        color: var(--color-black) !important;
        caret-color: initial !important;
        padding-bottom: 0;
        opacity: 1;
      }
    }
  }
}
</style>
